import api from "@/core/api";

export interface GoogleAuthPayload {
  authCode: string;
  redirectUri: string;
  invitationCode?: string;
  action?: "LOGIN" | "REGISTER";
}

export interface SocialAuthPayload {
  idToken: string;
  invitationCode?: string;
  action?: "LOGIN" | "REGISTER";
}

// Interfaz sugerida para las credenciales de Login
export interface LoginCredentials {
  email: string;
  password: string;
}

// Interfaz sugerida para los datos de registro (empresa o empleado)
export interface RegisterData {
  email: string;
  password?: string;
  companyCode?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  timezone?: string;
  // ... otros campos
}

export interface CompanyData {
  businessName: string;
  tradeName: string;
  taxIdentifier?: string;
  registrationNumber?: string;
  email: string;
  phoneNumber?: string;
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  industrySector?: string;
  description?: string;
}

export interface AdminEmployeeData {
  companyId: string;
  name: string;
  lastName: string;
  secondSurname?: string;
  code?: string;
  nationalIdentifier?: string;
  taxIdentifier?: string;
  ssn?: string;
  phoneNumber?: string;
}

export interface RefreshResponse {
  token: string;
  refreshToken: string;
}

class AuthService {
  /**
   * Envía las credenciales y, en caso de éxito, el interceptor
   * o el propio componente se encarga de almacenar el token.
   */
  async login(credentials: LoginCredentials) {
    const payload = { ...credentials, clientType: "WEB" };
    const response = await api.post("/auth/login", payload);

    if (response.data && response.data.token) {
      localStorage.setItem("access_token", response.data.token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async register(data: RegisterData) {
    const payload = { ...data, clientType: "WEB" };
    const response = await api.post("/auth/register", payload);

    if (response.data && response.data.token) {
      localStorage.setItem("access_token", response.data.token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async refreshSession(refreshToken: string): Promise<RefreshResponse> {
    const response = await api.post("/auth/refresh", { refreshToken });
    if (response.data && response.data.token) {
      localStorage.setItem("access_token", response.data.token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async checkEmail(email: string): Promise<{ exists: boolean }> {
    const response = await api.get(
      `/auth/check-email?email=${encodeURIComponent(email)}`
    );
    return response.data;
  }

  // --- Endpoints de Organización (Empresa) ---

  async createCompany(data: CompanyData) {
    const response = await api.post("/organization/companies", data);
    return response.data;
  }

  async validateCompanyFields(data: Partial<CompanyData>) {
    const response = await api.post("/organization/companies/validate", data);
    return response.data;
  }

  // --- Endpoints de Recursos Humanos (HR) ---

  async registerAdminAsEmployee(data: AdminEmployeeData) {
    const payload = { ...data, clientType: "WEB" };
    const response = await api.post("/personnel/employees/admin", payload);
    if (response.data && (response.data.token || response.data.jwtToken)) {
      localStorage.setItem(
        "access_token",
        response.data.token || response.data.jwtToken
      );
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async registerEmployeeWithCode(data: any) {
    const payload = { ...data, clientType: "WEB" };
    const response = await api.post(
      "/personnel/employees/register-with-code",
      payload
    );
    if (response.data && response.data.token) {
      localStorage.setItem("access_token", response.data.token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async getProfile() {
    // Al hacer esta petición GET, el Interceptor automáticamente
    // agregará "Authorization: Bearer <token>" a los headers.
    const response = await api.get("/auth/me");
    return response.data;
  }

  // --- Endpoints de Invitaciones ---
  async validateInvitationCode(code: string): Promise<{
    isValid: boolean;
    employeeId?: string | null;
    name?: string | null;
    lastName?: string | null;
    secondSurname?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  }> {
    const response = await api.get(
      `/invitations/validate?code=${encodeURIComponent(code)}`
    );
    return response.data;
  }

  // --- Endpoints de Autenticación Social (Upsert) ---

  async googleAuth(payload: GoogleAuthPayload) {
    const reqPayload = { ...payload, clientType: "WEB" };
    const response = await api.post("/auth/oauth/google", reqPayload);
    const token = response.data.token || response.data.jwtToken;
    if (token) {
      localStorage.setItem("access_token", token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }

  async microsoftAuth(payload: SocialAuthPayload) {
    const reqPayload = { ...payload, clientType: "WEB" };
    const response = await api.post("/auth/oauth/microsoft", reqPayload);
    const token = response.data.token || response.data.jwtToken;
    if (token) {
      localStorage.setItem("access_token", token);
    }
    if (response.data && response.data.refreshToken) {
      localStorage.setItem("refresh_token", response.data.refreshToken);
    }
    return response.data;
  }
}

// Exportamos una única instancia del servicio para ser reutilizada
export const authService = new AuthService();
